import { boxToPixels, type FontLike, layoutText } from "./layout.ts";
import type { CaptionStyle, TextLayer } from "./schema.ts";

export interface CaptionRenderInput {
  layers: TextLayer[];
  style: CaptionStyle;
  font: FontLike;
  /** Output size in pixels; the artwork is assumed to fill it exactly. */
  width: number;
  height: number;
}

/** Stroke width relative to font size: the classic image-macro outline. */
const STROKE_RATIO = 0.075;

/**
 * Caption geometry as SVG path markup (no <svg> wrapper). Text is converted
 * to outlines, so rendering never depends on fonts installed on the machine
 * and user text never enters the SVG as text (no markup injection surface).
 */
export function renderCaptionPaths(input: CaptionRenderInput): string {
  const { layers, style, font, width, height } = input;
  const parts: string[] = [];
  for (const layer of layers) {
    const raw = style.uppercase ? layer.text.toLocaleUpperCase() : layer.text;
    if (!raw.trim()) continue;
    const layout = layoutText(font, raw, boxToPixels(layer.box, width, height), layer.valign);
    if (layout.lines.length === 0) continue;
    const d = layout.lines
      .map((l) => font.getPath(l.text, l.x, l.y, layout.fontSize).toPathData(2))
      .join(" ");
    const sw = (layout.fontSize * STROKE_RATIO * 2).toFixed(2);
    parts.push(
      `<path d="${d}" fill="none" stroke="${style.stroke}" stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round"/>`,
      `<path d="${d}" fill="${style.fill}"/>`,
    );
  }
  return parts.join("");
}

/** A standalone transparent SVG of just the captions, at `width x height`. */
export function renderCaptionSvg(input: CaptionRenderInput): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${input.width}" height="${input.height}" viewBox="0 0 ${input.width} ${input.height}">${renderCaptionPaths(input)}</svg>`;
}

/* ------------------------------------------------------------------ */
/* Print placement                                                     */
/* ------------------------------------------------------------------ */

export type PrintFit = "contain-top" | "contain-center" | "cover";

export interface Placement {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Where the artwork sits inside the print area. Apparel anchors to the top of
 * the chest; mugs/posters center or fill.
 */
export function placeArtwork(
  art: { width: number; height: number },
  area: { width: number; height: number },
  fit: PrintFit,
): Placement {
  const scale =
    fit === "cover"
      ? Math.max(area.width / art.width, area.height / art.height)
      : Math.min(area.width / art.width, area.height / art.height);
  const width = Math.round(art.width * scale);
  const height = Math.round(art.height * scale);
  const left = Math.round((area.width - width) / 2);
  const top = fit === "contain-top" ? 0 : Math.round((area.height - height) / 2);
  return { left, top, width, height };
}

export function mmToPx(mm: number, dpi: number): number {
  return Math.round((mm / 25.4) * dpi);
}
